import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageIcon, Trash2, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import MediaUploader from "@/components/admin/MediaUploader";
import { api, absUrl, formatApiError } from "@/lib/api";
import { ACCEPT_IMAGES } from "@/lib/upload";
import { useSiteMedia } from "@/context/SiteMediaContext";

// Admin: assign real photos to the fixed placeholder slots across the site.
export default function SiteMediaPanel() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const { media, refresh } = useSiteMedia();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/site-media/slots");
      setSlots(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load media slots"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-400" /></div>;

  return (
    <div data-testid="site-media-panel">
      <p className="mb-5 text-sm text-neutral-400">Each slot below is a "Photo Coming Soon" panel on the public site. Upload a real Kensmarc Electric photo to replace it; remove it to show the placeholder again.</p>
      <div className="grid gap-4 md:grid-cols-2">
        {slots.map((s) => <SlotCard key={s.slot} slot={s} current={media[s.slot]} onChanged={refresh} />)}
      </div>
    </div>
  );
}

function SlotCard({ slot, current, onChanged }) {
  const [alt, setAlt] = useState(current?.alt || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAlt(current?.alt || ""); }, [current?.alt]);

  const assign = async (fileId, altText) => {
    setBusy(true);
    try {
      await api.put(`/admin/site-media/${slot.slot}`, { file_id: fileId, alt: altText });
      onChanged();
      toast.success(`${slot.label} updated`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not assign photo"));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/site-media/${slot.slot}`);
      onChanged();
      toast.success("Placeholder restored");
    } catch (err) {
      toast.error(formatApiError(err, "Could not remove photo"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#20202a] bg-[#101017] p-5" data-testid={`slot-card-${slot.slot}`}>
      <p className="font-semibold text-white">{slot.label}</p>
      <p className="mt-0.5 text-xs text-neutral-500">{slot.where}</p>

      <div className="mt-4 aspect-[16/10] overflow-hidden rounded-lg border border-[#2a2a34] bg-[#0b0b0f]">
        {current ? (
          <img src={absUrl(current.url)} alt={current.alt || ""} className="h-full w-full object-cover" data-testid={`slot-image-${slot.slot}`} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-neutral-600" data-testid={`slot-empty-${slot.slot}`}>
            <ImageIcon className="h-7 w-7" />
            <span className="text-xs">Showing placeholder on site</span>
          </div>
        )}
      </div>

      {current && (
        <div className="mt-3 flex gap-2">
          <Input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Describe the photo (alt text)" maxLength={200} className="border-[#2a2a34] bg-[#0f0f15] text-white" data-testid={`slot-alt-${slot.slot}`} />
          <button onClick={() => assign(current.file_id, alt)} disabled={busy} title="Save description" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#2a2a34] text-neutral-200 hover:border-amber-400/60 disabled:opacity-50" data-testid={`slot-save-alt-${slot.slot}`}><Check className="h-4 w-4" /></button>
          <button onClick={clear} disabled={busy} title="Remove photo" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-red-400/40 text-red-300 hover:bg-red-400/10 disabled:opacity-50" data-testid={`slot-remove-${slot.slot}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      )}

      <div className="mt-3">
        <MediaUploader
          accept={ACCEPT_IMAGES}
          multiple={false}
          label={current ? "Replace photo" : "Upload photo"}
          hint="JPG, PNG, WEBP, HEIC up to 15MB"
          onUploaded={(r) => assign(r.id, alt)}
          testId={`slot-uploader-${slot.slot}`}
        />
      </div>
    </div>
  );
}
